import { LayoutObjectType } from '@yard/app/api'
import { LayoutObjectStore } from '@yard/stores/LayoutObjectStore'
import { RoadNetworkStore } from '@yard/stores/road-network.store'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import {
  mapLayoutObjectFormDataToCreateCommand,
  mapLayoutObjectFormDataToUpdateCommand,
} from '../../helpers/layout-object.mapper'
import { ILayoutObjectFormData } from '../../models/yard-setup.model'
import { YardNodeViewStore } from './yard-node.view-store'

export class LayoutObjectViewStore extends YardNodeViewStore<
  ILayoutObjectFormData,
  LayoutObjectStore
> {
  editingType?: LayoutObjectType

  constructor(
    layoutObjectStore: LayoutObjectStore,
    private readonly roadNetworkStore: RoadNetworkStore,
  ) {
    super(layoutObjectStore)
    makeObservable(this, {
      editingType: observable,

      openStandardLayoutObjectFormDialog: action,
      openBackgroundLayoutObjectFormDialog: action,
      openGateFormDialog: action,
      openHoldingPointFormDialog: action,
      openHandoverAreaFormDialog: action,
      setSize: action,
      setPosition: action,
      setRotation: action,

      focusedLayoutObject: computed,
    })
  }

  openStandardLayoutObjectFormDialog = (id?: string) => {
    this.openFormDialog(id)
    this.editingType = LayoutObjectType.Unspecified
  }

  openBackgroundLayoutObjectFormDialog = (id?: string) => {
    this.openFormDialog(id)
    this.editingType = LayoutObjectType.Background
  }

  openGateFormDialog = (id?: string) => {
    this.openFormDialog(id)
    this.editingType = LayoutObjectType.Gate
  }

  openHoldingPointFormDialog = (id?: string) => {
    this.openFormDialog(id)
    this.editingType = LayoutObjectType.HoldingPoint
  }

  openHandoverAreaFormDialog = (id?: string) => {
    this.openFormDialog(id)
    this.editingType = LayoutObjectType.HandoverArea
  }

  setSize = (id: string, width: number, height: number) => {
    const layoutObject = this.entityStore.layoutObjects[id]

    if (layoutObject && (layoutObject.width !== width || layoutObject.height !== height)) {
      this.entityStore.updateLayoutObject({
        ...layoutObject,
        position: layoutObject.layoutPosition,
        width: Math.round(width),
        height: Math.round(height),
      })
    }
  }

  setPosition = (id: string, position: { x: number; y: number }) => {
    const layoutObject = this.entityStore.layoutObjects[id]

    if (
      layoutObject &&
      (layoutObject.layoutPosition.x !== position.x || layoutObject.layoutPosition.y !== position.y)
    ) {
      const layoutPosition = {
        ...layoutObject.layoutPosition,
        x: Math.round(position.x),
        y: Math.round(position.y),
      }

      this.entityStore.updateLayoutPosition({
        ...layoutPosition,
        layoutObjectId: id,
      })
    }
  }

  setRotation = (id: string, rotation: number) => {
    const layoutObject = this.entityStore.layoutObjects[id]
    if (!layoutObject) return

    const thetaNew = Math.round(rotation)
    if (layoutObject.layoutPosition.rotation === thetaNew) return

    const thetaOld = layoutObject.layoutPosition.rotation
    const layoutPosition = { ...layoutObject.layoutPosition, rotation: thetaNew }

    // Optimistically update the rotation in the local store so that rapid successive calls
    // read the correct thetaOld instead of the stale server-synced value.
    this.entityStore.updateStoreItem({ ...layoutObject, layoutPosition }, id)

    this.entityStore.updateLayoutPosition({ ...layoutPosition, layoutObjectId: id })
    this.rotateLinkedNodes(id, layoutObject, thetaOld, thetaNew)
  }

  private rotateLinkedNodes(
    id: string,
    layoutObject: { layoutPosition: { x: number; y: number }; width: number; height: number },
    thetaOld: number,
    thetaNew: number,
  ): void {
    const delta = (thetaNew - thetaOld) * (Math.PI / 180)
    const cx = layoutObject.layoutPosition.x + layoutObject.width / 2
    const cy = layoutObject.layoutPosition.y + layoutObject.height / 2
    const cos = Math.cos(delta)
    const sin = Math.sin(delta)

    const linkedNodes = this.roadNetworkStore.nodes.filter(n => n.layoutObjectId === id)

    for (const node of linkedNodes) {
      const dx = node.layoutPosition.x - cx
      const dy = node.layoutPosition.y - cy
      const newX = Math.round(cx + dx * cos - dy * sin)
      const newY = Math.round(cy + dx * sin + dy * cos)

      if (newX === node.layoutPosition.x && newY === node.layoutPosition.y) continue

      const updatedNode = {
        ...node,
        layoutPosition: { ...node.layoutPosition, x: newX, y: newY },
      }
      this.roadNetworkStore.updateNodeOptimistic(updatedNode)
      this.roadNetworkStore.debouncedUpdateNode(node.id)
    }
  }

  get focusedLayoutObject() {
    return this.editingId ? this.entityStore.layoutObjects[this.editingId] : undefined
  }

  isGateCodeTaken = (code: string): boolean => {
    const normalized = code.trim().toLowerCase()
    if (!normalized) return false

    return this.entityStore.items.some(
      obj =>
        obj.type === LayoutObjectType.Gate &&
        obj.id !== this.editingId &&
        (obj.code ?? '').toLowerCase() === normalized,
    )
  }

  async submit(formData: ILayoutObjectFormData): Promise<void> {
    if (this.editingId) {
      const updateCommand = mapLayoutObjectFormDataToUpdateCommand(
        formData,
        this.entityStore.layoutObjects[this.editingId],
      )
      await this.entityStore.updateLayoutObject(updateCommand)
    } else {
      const createCommand = mapLayoutObjectFormDataToCreateCommand(
        formData,
        this.editingType!,
        this.viewportCenter,
      )
      await this.entityStore.createLayoutObject(createCommand)
    }
  }

  async delete(id: string): Promise<void> {
    const linkedNodes = this.roadNetworkStore.nodes.filter(n => n.layoutObjectId === id)
    for (const node of linkedNodes) {
      this.roadNetworkStore.removeNodeOptimistic(node.id)
    }
    try {
      await this.entityStore.deleteLayoutObject(id)
      await Promise.all(linkedNodes.map(node => this.roadNetworkStore.deleteNode(node.id)))
    } catch (err) {
      runInAction(() => {
        for (const node of linkedNodes) {
          this.roadNetworkStore.addNodeOptimistic(node)
        }
      })
      throw err
    }
  }
}
